import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Skeleton,
  Stack,
  Paper,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import apiService from "../../../../../service/Api";

interface CrewMember {
  crew_member_id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  average_rating: number;
  review_count: number;
  roles?: string[];
  profile_picture?: string | null;
}

interface CrewPerformanceSummaryResponse {
  month: string;
  scope: string;
  rating_threshold: number;
  min_reviews_threshold: number;
  top_performers: CrewMember[];
  needs_attention: CrewMember[];
}

const getGradientByName = (name: string) => {
  const hash = name.split("").reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0);
  const colors = [
    ["#ff9a9e", "#fecfef"],
    ["#a1c4fd", "#c2e9fb"],
    ["#76b852", "#8DC26F"],
    ["#f12711", "#f5af19"],
    ["#a80077", "#667db6"],
    ["#3a7bd5", "#3a6073"],
    ["#f857a6", "#ff5858"],
    ["#ffd1ff", "#f107a3"],
    ["#11998e", "#38ef7d"],
  ];
  const idx = Math.abs(hash) % colors.length;
  return `linear-gradient(135deg, ${colors[idx][0]} 0%, ${colors[idx][1]} 100%)`;
};

// ─── Star renderer ────────────────────────────────────────────────────────────
const StarRating = ({ value, color }: { value: number; color: string }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (value >= i) {
      stars.push(<StarIcon key={i} sx={{ fontSize: 13, color }} />);
    } else if (value >= i - 0.5) {
      stars.push(<StarHalfIcon key={i} sx={{ fontSize: 13, color }} />);
    } else {
      stars.push(
        <StarBorderIcon key={i} sx={{ fontSize: 13, color: "rgba(255,255,255,0.2)" }} />,
      );
    }
  }
  return <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>{stars}</Box>;
};

// ─── Medal colours for top 3 ──────────────────────────────────────────────────
const getMedalBg = (index: number) => {
  if (index === 0) return "linear-gradient(135deg, #ffd700 0%, #ffa500 100%)"; // gold
  if (index === 1) return "linear-gradient(135deg, #e6e6e6 0%, #999999 100%)"; // silver
  if (index === 2) return "linear-gradient(135deg, #f5a623 0%, #a05a2c 100%)"; // bronze
  return "rgba(255, 255, 255, 0.08)";
};

// ─── Single row ───────────────────────────────────────────────────────────────
const CrewRow = ({
  member,
  index,
  mode,
}: {
  member: CrewMember;
  index: number;
  mode: "top" | "low";
}) => {
  const fullName = `${member.first_name} ${member.last_name}`;
  const initials = `${member.first_name.charAt(0)}${member.last_name.charAt(0)}`.toUpperCase();

  const isTop = mode === "top";
  const ratingColor = isTop ? "#38ef7d" : "#f43f5e"; // green vs red
  const medalBg = isTop ? getMedalBg(index) : "rgba(244, 63, 94, 0.12)";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderRadius: 3,
        bgcolor: "rgba(255, 255, 255, 0.04)",
        border: "1px solid rgba(255, 255, 255, 0.03)",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          bgcolor: "rgba(255, 255, 255, 0.07)",
          borderColor: "rgba(255, 255, 255, 0.08)",
        },
      }}
    >
      {/* Rank / medal */}
      <Box
        sx={{
          minWidth: 26,
          height: 26,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: medalBg,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 800,
            color: isTop && index <= 2 ? "#ffffff" : isTop ? "rgba(255,255,255,0.7)" : "#f43f5e",
          }}
        >
          {index + 1}
        </Typography>
      </Box>

      {/* Avatar */}
      <Avatar
        src={member.profile_picture || undefined}
        sx={{
          width: 38,
          height: 38,
          background: getGradientByName(fullName),
          fontWeight: 700,
          fontSize: "13px",
          color: "#ffffff",
          flexShrink: 0,
        }}
      >
        {initials}
      </Avatar>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={700}
          noWrap
          sx={{ color: "#ffffff", textTransform: "capitalize" }}
        >
          {member.first_name} {member.last_name.toLowerCase()}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.5} gap={0.5}>
          {member.roles?.map((r) => (
            <Chip
              key={r}
              label={r}
              size="small"
              sx={{
                fontSize: "9px",
                height: 16,
                backgroundColor: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.6)",
                border: "none",
                fontWeight: 600,
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Rating */}
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 800,
            color: ratingColor,
            lineHeight: 1.1,
            mb: 0.3,
          }}
        >
          {member.average_rating.toFixed(1)}
        </Typography>
        <StarRating value={member.average_rating} color={ratingColor} />
        <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.4)", mt: 0.5 }}>
          {member.review_count} review{member.review_count !== 1 ? "s" : ""}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Loading skeleton ─────────────────────────────────────────────────────────
const LoadingRows = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
    {[1, 2, 3].map((i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          bgcolor: "rgba(255,255,255,0.02)",
          borderRadius: 3,
        }}
      >
        <Skeleton variant="circular" width={26} height={26} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
        <Skeleton variant="circular" width={38} height={38} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="60%" height={16} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
          <Skeleton width="40%" height={12} sx={{ mt: 0.8, bgcolor: "rgba(255,255,255,0.05)" }} />
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Skeleton width={32} height={20} sx={{ ml: "auto", bgcolor: "rgba(255,255,255,0.05)" }} />
          <Skeleton width={60} height={12} sx={{ mt: 0.8, bgcolor: "rgba(255,255,255,0.05)" }} />
        </Box>
      </Box>
    ))}
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────
const CrewRatings: React.FC = () => {
  const [tab, setTab] = useState<0 | 1>(0);
  const [topCrew, setTopCrew] = useState<CrewMember[]>([]);
  const [lowCrew, setLowCrew] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPerformanceSummary = async () => {
      try {
        setLoading(true);
        const now = new Date();
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

        const res = await apiService.get<CrewPerformanceSummaryResponse>(
          `/api/management/dashboard/crew-performance-summary/?month=${month}`,
        );

        setTopCrew(res.data?.top_performers ?? []);
        setLowCrew(res.data?.needs_attention ?? []);
      } catch (err: unknown) {
        const errorMessage =
          (err as { message?: string })?.message ||
          "Failed to fetch crew performance summary.";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceSummary();
  }, []);

  const isTop = tab === 0;
  const crew = isTop ? topCrew : lowCrew;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        background: "linear-gradient(180deg, #0c152b 0%, #070d19 100%)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.4)",
        mt: 2,
        py: 2.5,
        px: 2.5,
        width: "100%",
        color: "#ffffff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            letterSpacing: "0.05em",
            color: "#ffffff",
            textTransform: "uppercase",
            fontSize: "14px",
          }}
        >
          Crew Ratings
        </Typography>
        <Box
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.08)",
            color: "rgba(255, 255, 255, 0.7)",
            px: 1.5,
            py: 0.5,
            borderRadius: 3,
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          This Month
        </Box>
      </Box>

      {/* Styled Gamified Tab Pills */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
        <Box
          onClick={() => setTab(0)}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 1.2,
            borderRadius: 3,
            cursor: "pointer",
            bgcolor: tab === 0 ? "rgba(255, 215, 0, 0.12)" : "rgba(255, 255, 255, 0.03)",
            border: "1px solid",
            borderColor: tab === 0 ? "rgba(255, 215, 0, 0.3)" : "rgba(255, 255, 255, 0.05)",
            color: tab === 0 ? "#ffd700" : "rgba(255, 255, 255, 0.4)",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: tab === 0 ? "rgba(255, 215, 0, 0.18)" : "rgba(255, 255, 255, 0.07)",
              color: tab === 0 ? "#ffd700" : "#ffffff",
            },
          }}
        >
          <EmojiEventsIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontWeight: 800, fontSize: "12px" }}>Top Rated</Typography>
        </Box>
        <Box
          onClick={() => setTab(1)}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 1.2,
            borderRadius: 3,
            cursor: "pointer",
            bgcolor: tab === 1 ? "rgba(244, 63, 94, 0.12)" : "rgba(255, 255, 255, 0.03)",
            border: "1px solid",
            borderColor: tab === 1 ? "rgba(244, 63, 94, 0.3)" : "rgba(255, 255, 255, 0.05)",
            color: tab === 1 ? "#f43f5e" : "rgba(255, 255, 255, 0.4)",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: tab === 1 ? "rgba(244, 63, 94, 0.18)" : "rgba(255, 255, 255, 0.07)",
              color: tab === 1 ? "#f43f5e" : "#ffffff",
            },
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontWeight: 800, fontSize: "12px" }}>Low Ratings</Typography>
        </Box>
      </Box>

      {/* Body List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, flexGrow: 1 }}>
        {loading ? (
          <LoadingRows />
        ) : error ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : crew.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography sx={{ color: "rgba(255,255,255,0.4)" }} variant="body2">
              No crew in this category yet.
            </Typography>
          </Box>
        ) : (
          crew.map((member, i) => (
            <CrewRow
              key={member.crew_member_id}
              member={member}
              index={i}
              mode={isTop ? "top" : "low"}
            />
          ))
        )}
      </Box>
    </Paper>
  );
};

export default CrewRatings;
